import _ from 'lodash';
import { getIntl } from 'umi';

import { persistCheckoutPaymentSessionId } from '@/pages/billing/checkout/checkoutHelpers';
import { bindServices } from '@/services/api';
import { getFiltersGlobalFilters, toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';

const api = bindServices('aiBillingCredits');

export const FORM_MODES = Object.freeze({
  MANUAL: 'manual',
  CHECKOUT: 'checkout',
});

const namespace = 'aiBillingCredits';
const fetchRoute = 'billing/ai/credits';
const rechargeRoute = 'billing/recharges/ai-credits';
const checkoutRoute = `${rechargeRoute}/checkout`;
const fetchParams = 'AiBillingCredit';
const usageFetchParams = 'AiBillingCreditUsage';
const usageFilterModel = 'aiBillingCreditUsage';

const initialPagination = { page: 1, count: 0, totalPages: 0 };

const initialCreditUsageState = {
  list: [],
  pagination: { ...initialPagination },
  orderBy: CoreQueries[usageFetchParams].paged.orderBy,
  creditGrantId: null,
};

const initialState = {
  list: [],
  listLoaded: false,
  pagination: initialPagination,
  orderBy: '',
  select: CoreQueries[fetchParams].paged.select,
  showForm: false,
  formMode: null,
  detail: false,
  usage: false,
  id: null,
  item: null,
  success: false,
  validationErrors: [],
  creditUsageSearchTable: '',
  creditUsage: { ...initialCreditUsageState },
};

const AiBillingCreditsModel = {
  namespace,
  state: initialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const params = {
          ...CoreQueries[fetchParams].paged,
          pageSize,
          ...payload,
        };
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params,
          readPriority: true,
        });
        yield put({
          type: 'queryData',
          payload: { ...res, limit: payload.limit ?? pageSize, page: payload.page ?? 1 },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: payload.detail ? CoreQueries[fetchParams]?.itemDetail : {},
        });
        const item = Array.isArray(res) ? res[0] : res;
        yield put({ type: 'queryItem', payload: item });
      } catch (e) {
        /*  */
      }
    },

    *fetchCreditUsage({ payload }, { call, put, select }) {
      try {
        const creditGrantId = payload.id;
        if (!creditGrantId) return;

        const { creditUsage, creditUsageSearchTable } = yield select((state) => state[namespace]);
        const globalPageSize = yield select((state) => state.global.pageSize);

        const pageSize = payload.limit ?? globalPageSize;
        const page = payload.page ?? 1;
        const search = payload.search !== undefined ? payload.search : creditUsageSearchTable ?? '';
        const orderBy =
          payload.orderBy ?? creditUsage.orderBy ?? CoreQueries[usageFetchParams].paged.orderBy;

        const filterStr = payload.filter
          ? getFiltersGlobalFilters({ [usageFilterModel]: payload.filter }, usageFilterModel)
          : null;

        const params = {
          ...CoreQueries[usageFetchParams].paged,
          pageSize,
          page,
          limit: pageSize,
          orderBy,
          ...(filterStr && { filter: filterStr }),
          ...(search && {
            search,
            searchSelect: CoreQueries[usageFetchParams].paged.searchSelect,
          }),
        };

        const res = yield call(api.getAll, {
          route: `${fetchRoute}/usage/${creditGrantId}`,
          params,
          readPriority: true,
        });

        yield put({
          type: 'queryCreditUsage',
          payload: {
            ...res,
            limit: pageSize,
            page,
            creditGrantId,
            orderBy,
          },
        });
      } catch (e) {
        /*  */
      }
    },

    *setOrderCreditUsage({ payload }, { put }) {
      yield put({ type: 'querySetOrderCreditUsage', payload });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'queryOrderBy', payload });
    },

    *fetchCreate({ payload }, { put, call, select }) {
      try {
        const canGrantManual = yield select((state) => state.aiBilling.canGrantManual);
        if (!canGrantManual) return;

        yield put({ type: 'success', payload: { success: false } });
        yield call(api.create, { route: rechargeRoute, data: payload.data });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors || [],
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchCheckout({ payload }, { put, call, select }) {
      try {
        const canEdit = yield select((state) => state.aiBilling.canEdit);
        if (!canEdit) return;

        yield put({ type: 'success', payload: { success: false } });
        const res = yield call(api.create, { route: checkoutRoute, data: payload.data });
        const checkoutUrl = res?.checkoutUrl ?? res?.CheckoutUrl;
        const paymentSessionId = res?.paymentSessionId ?? res?.PaymentSessionId;

        persistCheckoutPaymentSessionId(paymentSessionId);

        if (!checkoutUrl) {
          throw new Error(
            getIntl().formatMessage({
              id: 'aiBilling.grants.rechargeCredits.error.missingCheckoutUrl',
            }),
          );
        }

        window.location.assign(checkoutUrl);
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors || [],
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },
  },

  reducers: {
    queryData(state, { payload }) {
      return {
        ...state,
        list: payload.items,
        listLoaded: true,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    queryOrderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    queryItem(state, { payload }) {
      return {
        ...state,
        item: payload,
      };
    },

    queryShowForm(state, { payload }) {
      const isGrantForm = payload.state && !payload.detail && !payload.usage;

      return {
        ...state,
        showForm: payload.state,
        formMode: isGrantForm ? payload.mode ?? null : null,
        detail: payload.detail ?? false,
        usage: payload.usage ?? false,
        id: payload.id ?? null,
        item: payload.item ?? null,
        success: false,
        validationErrors: [],
        ...(!payload.state && {
          id: null,
          formMode: null,
          detail: false,
          usage: false,
          creditUsageSearchTable: '',
          creditUsage: { ...initialCreditUsageState },
        }),
        ...(payload.state &&
          payload.usage && {
            creditUsageSearchTable: '',
            creditUsage: {
              ...initialCreditUsageState,
              creditGrantId: payload.id ?? null,
            },
          }),
      };
    },

    queryCreditUsage(state, { payload }) {
      return {
        ...state,
        creditUsage: {
          ...state.creditUsage,
          list: payload.items ?? [],
          creditGrantId: payload.creditGrantId ?? state.creditUsage.creditGrantId,
          orderBy: payload.orderBy ?? state.creditUsage.orderBy,
          pagination: _.merge(state.creditUsage.pagination, toPaginate(payload)),
        },
      };
    },

    setCreditUsageSearchTable(state, { payload }) {
      return {
        ...state,
        creditUsageSearchTable: payload ?? '',
      };
    },

    querySetOrderCreditUsage(state, { payload }) {
      return {
        ...state,
        creditUsage: {
          ...state.creditUsage,
          orderBy: payload.value,
        },
      };
    },

    success(state, { payload }) {
      return {
        ...state,
        ...payload,
        success: payload?.success ?? payload,
        validationErrors: [],
      };
    },

    errors(state, { payload }) {
      return {
        ...state,
        validationErrors: payload.validationErrors || [],
        message: payload.message,
      };
    },

    reset() {
      return { ...initialState, pagination: { ...initialPagination } };
    },
  },
};

export default AiBillingCreditsModel;
